// Cross-tab notification that the incident list has changed.
//
// Why this exists: selecting an incident opens the edit page in a NEW TAB
// (see openThemedTab in utils/theme.js). Each tab builds its own QueryClient
// in src/index.js, so the queryClient.invalidateQueries(['local-incidents'])
// the combine mutation fires only ever touches the cache of the tab the
// combine happened in. The listing tab is never told anything, so it keeps
// showing the merged-away incident until it is reloaded by hand.
//
// This is the same BroadcastChannel-with-localStorage-fallback approach
// crossTabLogout.js already uses, kept deliberately small: it carries no
// payload beyond "something about incidents changed", and each listening tab
// decides what to refetch.
class CrossTabIncidents {
    constructor() {
        this.channel = null
        this.storageKey = 'weatec_incidents_changed_event'
        this.channelName = 'weatec_incidents_channel'

        if (typeof BroadcastChannel !== 'undefined') {
            try {
                this.channel = new BroadcastChannel(this.channelName)
            } catch (error) {
                console.warn(
                    'Cross-tab incidents: BroadcastChannel unavailable, falling back to localStorage events'
                )
            }
        }
    }

    /** Tell every other tab that the incident list is out of date. */
    broadcastIncidentsChanged(reason = 'unknown') {
        const event = { type: 'INCIDENTS_CHANGED', reason, timestamp: Date.now() }

        if (this.channel) {
            try {
                this.channel.postMessage(event)
            } catch (error) {
                console.error('Cross-tab incidents: broadcast failed:', error)
            }
        }

        // localStorage fallback. Writing then removing is what fires the
        // `storage` event in the other tabs; the key itself is never read.
        try {
            localStorage.setItem(this.storageKey, JSON.stringify(event))
            localStorage.removeItem(this.storageKey)
        } catch (error) {
            console.error('Cross-tab incidents: localStorage broadcast failed:', error)
        }
    }

    /**
     * Run `callback` whenever another tab reports a change.
     * Returns an unsubscribe function.
     */
    onIncidentsChanged(callback) {
        const handleMessage = (event) => {
            if (event?.data?.type === 'INCIDENTS_CHANGED') callback(event.data)
        }
        const handleStorage = (event) => {
            // The remove() half of the broadcast also fires here with a null
            // newValue - ignore that one so the callback runs once per event.
            if (event.key !== this.storageKey || !event.newValue) return
            try {
                const data = JSON.parse(event.newValue)
                if (data?.type === 'INCIDENTS_CHANGED') callback(data)
            } catch {
                /* malformed payload from another tab - nothing to do */
            }
        }

        if (this.channel) this.channel.addEventListener('message', handleMessage)
        window.addEventListener('storage', handleStorage)

        return () => {
            if (this.channel) this.channel.removeEventListener('message', handleMessage)
            window.removeEventListener('storage', handleStorage)
        }
    }
}

const crossTabIncidents = new CrossTabIncidents()
export default crossTabIncidents
